/**
 * Colour helpers for the device fallback screens.
 *
 * The fallback palette is campaign-configurable, so every scrim and border on these screens
 * is some fraction of a token rather than a literal. These keep that arithmetic in one place
 * instead of scattering hex-with-alpha string concatenation through the components.
 */

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `"31, 17, 46"` for a hex colour, or null for anything this cannot be taken apart from. */
export const toRgbChannels = (color: string): string | null => {
  if (!HEX_COLOR.test(color)) return null;

  const digits = color.slice(1);
  const full = digits.length === 3 ? digits.replace(/./g, (d) => d + d) : digits;
  const value = parseInt(full, 16);
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
};

/**
 * A campaign colour at a given opacity.
 *
 * A campaign could set any CSS colour, and one we cannot take apart cannot be faded. Those
 * fall back to the colour as given, which renders opaque: heavier than intended, but it
 * keeps the element visible rather than dropping it out of the composition entirely.
 */
export const withAlpha = (color: string, alpha: number): string => {
  const channels = toRgbChannels(color);
  return channels ? `rgba(${channels}, ${alpha})` : color;
};
